import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import RadioInput from 'LuxKit/Radio/RadioInput'
import SwitchButton from 'LuxKit/Switch/SwitchButton'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import styled from 'styled-components'
import { rem } from 'polished'
import { FlightsClassTypes } from 'constants/flight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { EmptyArray } from 'lib/array/arrayUtils'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

interface Props {
  nights: number
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  fareClass?: FlightsClassTypes
  onFareClassChange?: (fareClass: FlightsClassTypes) => void
  luxPlusEnabled?: boolean
  onLuxPlusToggle?: (enabled: boolean) => void
  availableFareClasses?: Array<FlightsClassTypes>
}

/**
 * Header actions for Charter Bundle Calendar Modal
 * Contains duration dropdown and LuxPlus toggle
 */
function CharterBundleCalendarActions(props: Props) {
  const {
    nights,
    onDurationChange,
    durationOptions,
    fareClass,
    onFareClassChange,
    luxPlusEnabled = true,
    onLuxPlusToggle,
    availableFareClasses = EmptyArray,
  } = props

  const { charterFareCabinLabelMap } = useCharterFlightLabels()
  const isLuxRowEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.luxRow)

  const baseFareClassOptions = useMemo(() => {
    const baseOptions = [FlightsClassTypes.ECONOMY, FlightsClassTypes.PREMIUM, FlightsClassTypes.BUSINESS]
    if (isLuxRowEnabled) {
      return [FlightsClassTypes.ECONOMY, FlightsClassTypes.PREMIUM, FlightsClassTypes.LUXROW, FlightsClassTypes.BUSINESS]
    }
    return baseOptions
  }, [isLuxRowEnabled])
  const fareClassOptions = useMemo(() => {
    const allowedFareClasses = availableFareClasses.length > 0 ? availableFareClasses : baseFareClassOptions
    return baseFareClassOptions.filter(option => allowedFareClasses.includes(option))
  }, [availableFareClasses, baseFareClassOptions])

  const [isFareClassDropdownOpen, setIsFareClassDropdownOpen] = useState(false)
  const fareClassTriggerRef = useRef<HTMLButtonElement>(null)

  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = useState(false)
  const durationTriggerRef = useRef<HTMLButtonElement>(null)

  // Auto-switch to available duration if current selection is no longer available
  // This handles the case where a user's selected duration sells out
  useEffect(() => {
    const isCurrentDurationAvailable = durationOptions.some(opt => opt.value === nights)
    if (!isCurrentDurationAvailable && durationOptions.length > 0) {
      onDurationChange(durationOptions[0].value)
    }
  }, [nights, durationOptions, onDurationChange])

  const handleDurationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newDuration = parseInt(event.target.value, 10)
    onDurationChange(newDuration)
    setIsDurationDropdownOpen(false)
  }, [onDurationChange])

  const toggleDurationDropdown = useCallback(() => {
    setIsDurationDropdownOpen(prev => !prev)
  }, [])

  const closeDurationDropdown = useCallback(() => {
    setIsDurationDropdownOpen(false)
  }, [])

  const handleLuxPlusToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onLuxPlusToggle?.(event.currentTarget.checked)
  }, [onLuxPlusToggle])

  const canOpenFareClassDropdown = fareClassOptions.length > 1
  const toggleFareClassDropdown = useCallback(() => {
    if (!canOpenFareClassDropdown) {
      return
    }
    setIsFareClassDropdownOpen(prev => !prev)
  }, [canOpenFareClassDropdown])

  const closeFareClassDropdown = useCallback(() => {
    setIsFareClassDropdownOpen(false)
  }, [])

  const handleFareClassChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedClass = event.currentTarget.value as FlightsClassTypes
    onFareClassChange?.(selectedClass)
    setIsFareClassDropdownOpen(false)
  }, [onFareClassChange])

  const shouldShowFareClassFilter = canOpenFareClassDropdown && !!onFareClassChange

  return (
    <Group direction="horizontal" verticalAlign="center" gap={8}>
      {shouldShowFareClassFilter && (
        <div>
          <FilterChip
            size="medium"
            onClick={toggleFareClassDropdown}
            endIcon={<DropdownChevron open={isFareClassDropdownOpen} />}
            ref={fareClassTriggerRef}
          >
            {fareClass ? charterFareCabinLabelMap[fareClass] : 'Select cabin'}
          </FilterChip>
          {shouldShowFareClassFilter && (
            <DropdownList
              size="S"
              open={isFareClassDropdownOpen}
              onClose={closeFareClassDropdown}
              anchorRef={fareClassTriggerRef}
              triggerRef={fareClassTriggerRef}
              portaled
            >
              <RadioGroup direction="vertical" gap={16}>
                {fareClassOptions.map((option) => (
                  <RadioInput
                    key={option}
                    checked={fareClass === option}
                    onChange={handleFareClassChange}
                    name="charterFareClass"
                    value={option}
                  >
                    {charterFareCabinLabelMap[option]}
                  </RadioInput>
                ))}
              </RadioGroup>
            </DropdownList>
          )}
        </div>
      )}
      {/* Only show duration dropdown if there are multiple duration options */}
      {durationOptions.length > 1 && (
        <div>
          <FilterChip
            size="medium"
            onClick={toggleDurationDropdown}
            endIcon={<DropdownChevron open={isDurationDropdownOpen} />}
            ref={durationTriggerRef}
          >
            {durationOptions.find(option => option.value === nights)?.label || '5 nights & flights'}
          </FilterChip>
          <DropdownList
            size="S"
            open={isDurationDropdownOpen}
            onClose={closeDurationDropdown}
            anchorRef={durationTriggerRef}
            triggerRef={durationTriggerRef}
            portaled
          >
            <RadioGroup direction="vertical" gap={16}>
              {durationOptions.map((option) => (
                <RadioInput
                  key={option.value}
                  checked={nights === option.value}
                  onChange={handleDurationChange}
                  name="durationNights"
                  value={option.value}
                >
                  {option.label}
                </RadioInput>
              ))}
            </RadioGroup>
          </DropdownList>
        </div>
      )}
      {config.BRAND === LUXURY_ESCAPES && (
        <ProductPaletteProvider palette="lux-plus">
          <SwitchButton
            checked={luxPlusEnabled}
            data-testid="luxPlus-toggle"
            onChange={handleLuxPlusToggle}
          >
            <LuxPlusLogo inline height={10} />
          </SwitchButton>
        </ProductPaletteProvider>
      )}
    </Group>
  )
}

export default CharterBundleCalendarActions
